import { useIntl } from 'react-intl';

import { handlingUnitSectionMessages } from './handling-unit-section-messages';
import styles from './HandlingUnitSection.module.css';
import { unitMessages } from '../../../../utils/unit-messages';
import { convertWeight } from '../../../../utils/weight-utils';
import { FREIGHT_CLASS_OPTIONS } from '../../../freight-browser/components/freight-form/freight-class-options';
import { DetailSection } from '../detail-section/DetailSection';
import { MeasurementCell } from '../measurement-cell/MeasurementCell';

import type { DimensionUnit } from '../../../../types/dimensions';
import type { LtlCommodity, LtlHandlingUnit, LtlShipment } from '../../../../types/ltl';
import type { WeightUnit } from '../../../../types/weight';
import type { MessageDescriptor } from 'react-intl';

type HandlingUnitSectionProps = {
  shipment: LtlShipment;
  displayDimensionUnit: DimensionUnit;
  displayWeightUnit: WeightUnit;
};

const DIMENSION_UNIT_ABBREVIATION: Record<DimensionUnit, MessageDescriptor> = {
  Inches: unitMessages.inches,
  Centimeters: unitMessages.centimeters,
};

const WEIGHT_UNIT_ABBREVIATION: Record<WeightUnit, MessageDescriptor> = {
  Pounds: unitMessages.pounds,
  Ounces: unitMessages.ounces,
  Kilograms: unitMessages.kilograms,
  Grams: unitMessages.grams,
};

/** Maps a stored freight class enum (e.g. `_77_5`) to its display label (`77.5`). */
const freightClassLabel = (freightClass: LtlCommodity['freightClass']): string =>
  FREIGHT_CLASS_OPTIONS.find((option) => option.value === freightClass)?.label ?? '';

/**
 * Left-column Handling Units card. Renders each handling unit's dimensions and
 * stackability, plus a gray inner card per commodity with its class, converted
 * total weight, NMFC code and piece details. Store-agnostic — props only.
 */
export const HandlingUnitSection = ({ shipment, displayDimensionUnit, displayWeightUnit }: HandlingUnitSectionProps) => {
  const intl = useIntl();

  const dimensionUnitLabel = intl.formatMessage(DIMENSION_UNIT_ABBREVIATION[displayDimensionUnit]);
  const weightUnitLabel = intl.formatMessage(WEIGHT_UNIT_ABBREVIATION[displayWeightUnit]);

  const formatDimension = (value: number) =>
    intl.formatMessage(handlingUnitSectionMessages.dimensionValue, { value, unit: dimensionUnitLabel });

  const renderCommodity = (commodity: LtlCommodity, index: number) => {
    // Commodity weights are stored in each commodity's own weightUnit (the wizard
    // gotcha); convert to the seller's display unit before showing.
    const convertedWeight = convertWeight(commodity.weight, commodity.weightUnit ?? displayWeightUnit, displayWeightUnit);
    const roundedWeight = Math.round(convertedWeight * 100) / 100;

    return (
      <div className={styles.commodityCard} key={commodity.id}>
        <span className={styles.commodityLabel}>
          {intl.formatMessage(handlingUnitSectionMessages.commodityLabel, { number: index + 1 })}
        </span>
        <div className={styles.measurementRow}>
          <MeasurementCell
            label={intl.formatMessage(handlingUnitSectionMessages.freightClass)}
            value={freightClassLabel(commodity.freightClass)}
          />
          <MeasurementCell
            label={intl.formatMessage(handlingUnitSectionMessages.totalWeight)}
            value={intl.formatMessage(handlingUnitSectionMessages.weightValue, { value: roundedWeight, unit: weightUnitLabel })}
          />
          <MeasurementCell label={intl.formatMessage(handlingUnitSectionMessages.nmfcCode)} value={commodity.nmfcCode} />
          <MeasurementCell label={intl.formatMessage(handlingUnitSectionMessages.pieceType)} value={commodity.pieceType} />
          <MeasurementCell label={intl.formatMessage(handlingUnitSectionMessages.pieceQuantity)} value={commodity.numberOfPieces} />
        </div>
      </div>
    );
  };

  const renderUnit = (unit: LtlHandlingUnit, index: number) => (
    <div className={styles.unit} key={unit.id}>
      <span className={styles.unitLabel}>
        {intl.formatMessage(handlingUnitSectionMessages.unitLabel, { number: index + 1, packaging: unit.packagingType })}
      </span>
      <div className={styles.measurementRow}>
        <MeasurementCell label={intl.formatMessage(handlingUnitSectionMessages.quantity)} value={unit.quantity} />
        <MeasurementCell label={intl.formatMessage(handlingUnitSectionMessages.width)} value={formatDimension(unit.width)} />
        <MeasurementCell label={intl.formatMessage(handlingUnitSectionMessages.length)} value={formatDimension(unit.length)} />
        <MeasurementCell label={intl.formatMessage(handlingUnitSectionMessages.height)} value={formatDimension(unit.height)} />
        <MeasurementCell
          label={intl.formatMessage(handlingUnitSectionMessages.stackable)}
          value={intl.formatMessage(unit.isStackable ? handlingUnitSectionMessages.stackableYes : handlingUnitSectionMessages.stackableNo)}
        />
      </div>
      <div className={styles.commodities}>{unit.commodities.map(renderCommodity)}</div>
    </div>
  );

  return (
    <DetailSection testId='handling-unit-section' title={intl.formatMessage(handlingUnitSectionMessages.sectionTitle)}>
      <div className={styles.units}>{shipment.handlingUnits.map(renderUnit)}</div>
    </DetailSection>
  );
};
